import {
  ACCESS_METHODS,
  BANDWIDTH_OPTIONS,
  BOOT_ARTIFACT_SOURCES,
  INFERENCE_MODES,
  INTERCONNECT_OPTIONS,
  SERVICE_OFFERINGS,
  TERM_LENGTHS,
  TIMELINES,
  TRAFFIC_PATTERNS,
  USE_CASES,
  describeCapacity,
  hasInference,
  hasInfrastructure,
  legacyConnectionField,
  legacyCustomerOsFields,
  type CapacityRequestAnswers,
} from "@/shared/capacity-request";
import { STORAGE_TIERS, tierCapacityLabel } from "@/shared/storage-tiers";

/** The editable steps a summary "Edit" link can jump to (excludes "review"). */
export type CapacitySummaryStep = "qualify" | "infrastructure" | "modules" | "inference" | "legal";

/** The subset of a capacity request the read-only summary renders. */
export interface CapacitySummaryData {
  company: string;
  contactName: string;
  role: string;
  answers: CapacityRequestAnswers;
}

function optionLabel(options: readonly { id: string; label: string }[], id: string): string {
  return options.find((option) => option.id === id)?.label ?? id;
}

function optionalLabel(
  options: readonly { id: string; label: string }[],
  id?: string,
): string | undefined {
  return id ? optionLabel(options, id) : undefined;
}

function multiLabel(
  options: readonly { id: string; label: string }[],
  ids?: string[],
): string | undefined {
  if (!ids?.length) return undefined;
  return ids.map((id) => optionLabel(options, id)).join(", ");
}

function storageTierLabel(id?: string): string | undefined {
  if (!id) return undefined;
  const tier = STORAGE_TIERS.find((option) => option.id === id);
  return tier ? `${tier.name} (${tierCapacityLabel(tier)})` : id;
}

function SummaryRow({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="grid gap-0.5">
      <span className="text-muted-foreground text-xs">{label}</span>
      <div className="text-sm font-medium">
        {value === undefined || value === null || value === "" ? (
          <span className="text-muted-foreground font-normal">Not provided</span>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

// Inquiries submitted before the customer-managed OS module was reworked stored
// handoff / provisioning-method / out-of-band answers. The staff detail view
// renders raw historical answers, so keep a read-only fallback that still labels
// those retired fields instead of silently hiding submitted intake data. The
// label maps + resolution live in the shared module so the submission email
// renders the same rows.
function LegacyConnectionRow({ answers }: { answers: CapacityRequestAnswers }) {
  const field = legacyConnectionField(answers);
  if (!field) return null;
  return <SummaryRow label={field.label} value={field.value} />;
}

function LegacyCustomerOsRows({ answers }: { answers: CapacityRequestAnswers }) {
  return (
    <>
      {legacyCustomerOsFields(answers).map((field) => (
        <SummaryRow key={field.label} label={field.label} value={field.value} />
      ))}
    </>
  );
}

function SummarySection({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-3 rounded-md border p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="text-primary text-xs font-medium underline underline-offset-4"
          >
            Edit
          </button>
        ) : null}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

/**
 * Read-only, section-by-section view of a capacity request. Used both in the
 * public intake review/confirmation steps (with per-section Edit links via
 * `onEdit`) and in the staff inquiry detail (read-only, no `onEdit`).
 */
export function CapacitySummary({
  data,
  email,
  onEdit,
}: {
  data: CapacitySummaryData;
  email: string;
  onEdit?: (step: CapacitySummaryStep) => void;
}) {
  const answers = data.answers;
  const infrastructure = answers.infrastructure ?? {};
  const customerOs = answers.customerManagedOs ?? {};
  const inference = answers.inference ?? {};
  const legal = answers.legal ?? {};

  const showInfrastructure = hasInfrastructure(answers);
  const managedOsSelected = answers.services?.includes("managed_os_bare_metal") ?? false;
  const customerOsSelected = answers.services?.includes("customer_os_bare_metal") ?? false;
  const showModules = managedOsSelected || customerOsSelected;
  const showInference = hasInference(answers);

  const useCases = multiLabel(USE_CASES, answers.useCases);
  const services = multiLabel(SERVICE_OFFERINGS, answers.services);

  return (
    <div className="grid gap-4">
      <SummarySection title="Contact" onEdit={onEdit ? () => onEdit("qualify") : undefined}>
        <SummaryRow label="Company" value={data.company} />
        <SummaryRow label="Contact name" value={data.contactName} />
        <SummaryRow label="Role" value={data.role} />
        <SummaryRow label="Email" value={email} />
      </SummarySection>

      <SummarySection title="Requirements" onEdit={onEdit ? () => onEdit("qualify") : undefined}>
        <SummaryRow
          label="Use cases"
          value={
            answers.useCases?.includes("other") && answers.otherUseCase
              ? `${useCases} (${answers.otherUseCase})`
              : useCases
          }
        />
        <SummaryRow
          label="Services"
          value={
            answers.services?.includes("other") && answers.otherService
              ? `${services} (${answers.otherService})`
              : services
          }
        />
        <SummaryRow
          label="Capacity"
          value={answers.nodeCount ? describeCapacity(answers.nodeCount) : undefined}
        />
        <SummaryRow label="Timeline" value={optionalLabel(TIMELINES, answers.timeline)} />
        <SummaryRow label="Term length" value={optionalLabel(TERM_LENGTHS, answers.termLength)} />
      </SummarySection>

      {showInfrastructure ? (
        <SummarySection
          title="Infrastructure"
          onEdit={onEdit ? () => onEdit("infrastructure") : undefined}
        >
          <SummaryRow
            label="Workload interconnect"
            value={optionalLabel(INTERCONNECT_OPTIONS, infrastructure.interconnect)}
          />
          <LegacyConnectionRow answers={answers} />
          <SummaryRow
            label="Bandwidth"
            value={optionalLabel(BANDWIDTH_OPTIONS, infrastructure.bandwidth)}
          />
          <SummaryRow
            label="High-performance storage"
            value={storageTierLabel(infrastructure.storageTier)}
          />
          <SummaryRow
            label="Access method"
            value={multiLabel(ACCESS_METHODS, infrastructure.accessMethods)}
          />
          {infrastructure.accessMethods?.includes("public_node_ips") ? (
            <SummaryRow label="Public IPs" value={infrastructure.publicIpCount} />
          ) : null}
        </SummarySection>
      ) : null}

      {showModules ? (
        <SummarySection
          title="Offering module"
          onEdit={onEdit ? () => onEdit("modules") : undefined}
        >
          {managedOsSelected ? (
            <SummaryRow
              label="Managed-OS additional requirements"
              value={answers.managedOs?.additionalRequirements}
            />
          ) : null}
          {customerOsSelected ? (
            <>
              <SummaryRow
                label="OS / boot artifacts served from"
                value={optionalLabel(BOOT_ARTIFACT_SOURCES, customerOs.bootArtifactSource)}
              />
              <LegacyCustomerOsRows answers={answers} />
            </>
          ) : null}
        </SummarySection>
      ) : null}

      {showInference ? (
        <SummarySection title="Inference" onEdit={onEdit ? () => onEdit("inference") : undefined}>
          <SummaryRow label="Models" value={inference.models} />
          <SummaryRow label="Serving type" value={optionalLabel(INFERENCE_MODES, inference.mode)} />
          <SummaryRow
            label="Traffic pattern"
            value={optionalLabel(TRAFFIC_PATTERNS, inference.trafficPattern)}
          />
        </SummarySection>
      ) : null}

      <SummarySection title="Legal" onEdit={onEdit ? () => onEdit("legal") : undefined}>
        <SummaryRow
          label="US eligibility attested"
          value={legal.eligibilityAttested ? "Yes" : undefined}
        />
        <SummaryRow
          label="Additional diligence acknowledged"
          value={legal.additionalDiligenceAcknowledged ? "Yes" : undefined}
        />
      </SummarySection>
    </div>
  );
}
